import React, { useMemo, useState } from 'react';
import { FaPlay } from 'react-icons/fa6';
import { ColumnsType } from 'antd/es/table';
import { toast } from 'react-toastify';
import { Tooltip } from 'antd';

import {
  useGetSegmentationLabelsQuery,
  useGetSegmentationReferencesQuery,
  useGetKeypointLabelsQuery,
  useGetPerspectivesQuery,
} from '../../../services/dicomService';

import {
  SegmentsContainer,
  HeaderRow,
  SegmentsLabel,
  ExpandIcon,
  StyledTable,
  TabButton,
  ClickableUUID,
} from './styled';

import { Perspective, ApiMediaData, KeypointLabel, KeypointCollectionLabel } from 'types/labels';
import {
  type ListPrefs,
  type SortKey,
  type StatusFilter,
  featureName,
  filterByStatus,
  readPrefs,
  reviewStatus,
  sortRows,
  statusCounts,
  technicianName,
  writePrefs,
} from './listing';

export interface SegmentationPayload {
  // maskUrl removed - we will use vector `paths` only for segmentations/references
  maskIcid: string;
  frameUrl: string;
  frameIcid: string;
  frameExtension: string;
  paths?: string[];
}

export interface SegmentsProps {
  mediaUuid: string;
  dicomId: number;
  onSegmentationClick: (payload: SegmentationPayload) => void;
  onKeypointClick?: (payload: {
    frameUrl: string;
    frameIcid: string;
    frameExtension: string;
    keypoints: KeypointLabel[];
  }) => void;
  /** Start expanded (default true). Nothing is fetched while collapsed. */
  defaultExpanded?: boolean;
  /** ICID of the mask (or keypoint frame) shown in the viewer, highlighted in the list. */
  selectedIcid?: string | null;
  /**
   * 'dock': compact list for the side panel next to the viewer (fewer columns, the list
   * scrolls inside the panel); 'panel' (default): full table.
   */
  layout?: 'panel' | 'dock';
  /**
   * Moves the list beside the viewer (panel) or back below it (dock). The button only
   * appears once someone is exploring labels, so the default page is unchanged.
   */
  onToggleSide?: () => void;
  /** Tab to open on, and a callback so the page can carry it across panel ↔ dock. */
  defaultTab?: LabelTab;
  onTabChange?: (tab: LabelTab) => void;
}

export type LabelTab = 'segmentations' | 'keypoints' | 'perspectives' | 'references';

const SORTS: { key: SortKey; label: string }[] = [
  { key: 'date', label: 'Date' },
  { key: 'feature', label: 'Feature' },
  { key: 'technician', label: 'Technician' },
];
const STATUSES: { key: StatusFilter; label: string }[] = [
  { key: 'all', label: 'All' },
  { key: 'accepted', label: 'Accepted' },
  { key: 'rejected', label: 'Rejected' },
  { key: 'unreviewed', label: 'Unreviewed' },
];

/** Accepted / Rejected / Unreviewed chip; a rejection shows the reviewer's reason on hover. */
export const ReviewBadge: React.FC<{ record: any }> = ({ record }) => {
  const status = reviewStatus(record);
  const r = record?.review;
  const label =
    status === 'accepted' ? 'Accepted' : status === 'rejected' ? 'Rejected' : 'Unreviewed';
  const detail = [
    r?.comment && `“${r.comment}”`,
    r?.reviewer && `by ${r.reviewer}`,
    r?.reviewed_at && new Date(r.reviewed_at).toLocaleDateString(),
  ]
    .filter(Boolean)
    .join(' · ');
  const chip = <span className={`ax-review ax-review-${status}`}>{label}</span>;
  return detail ? <Tooltip title={detail}>{chip}</Tooltip> : chip;
};

const PlayIcon: React.FC<{ size?: number }> = props => {
  const Icon = FaPlay as React.ComponentType<{ size?: number }>;
  return <Icon {...props} />;
};

const Segments: React.FC<SegmentsProps> = ({
  mediaUuid,
  dicomId,
  onSegmentationClick,
  onKeypointClick,
  defaultExpanded = true,
  selectedIcid = null,
  layout = 'panel',
  onToggleSide,
  defaultTab = 'segmentations',
  onTabChange,
}) => {
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);
  const [prefs, setPrefsState] = useState<ListPrefs>(readPrefs);
  const setPrefs = (patch: Partial<ListPrefs>) =>
    setPrefsState(prev => {
      const next = { ...prev, ...patch };
      writePrefs(next);
      return next;
    });
  const dock = layout === 'dock';
  const [activeTab, setActiveTabState] = useState<LabelTab>(defaultTab);
  const setActiveTab = (tab: LabelTab) => {
    setActiveTabState(tab);
    onTabChange?.(tab);
  };

  const { data: segmentationsData = [], isFetching: isSegFetching } = useGetSegmentationLabelsQuery(
    mediaUuid,
    {
      // Only fetch if mediaUuid exists AND we are on the segmentations tab
      skip: !isExpanded || !mediaUuid || activeTab !== 'segmentations',
    }
  );

  const { data: keypointCollectionData = [], isFetching: isKeypointFetching } =
    useGetKeypointLabelsQuery(mediaUuid, {
      skip: !isExpanded || !mediaUuid || activeTab !== 'keypoints',
    });

  const { data: perspectivesData = [], isFetching: isPersFetching } = useGetPerspectivesQuery(
    dicomId,
    {
      skip: !isExpanded || !dicomId || activeTab !== 'perspectives',
    }
  );

  const { data: segRefsData = [], isFetching: isSegRefsFetching } =
    useGetSegmentationReferencesQuery(mediaUuid, {
      skip: !isExpanded || !mediaUuid || activeTab !== 'references',
    });

  const baseRows: any[] =
    activeTab === 'keypoints'
      ? keypointCollectionData
      : activeTab === 'references'
        ? segRefsData
        : activeTab === 'segmentations'
          ? segmentationsData
          : [];
  const counts = useMemo(() => statusCounts(baseRows), [baseRows]);
  // References have no technician; sort them by date instead.
  const sortKey: SortKey =
    activeTab === 'references' && prefs.sortKey === 'technician' ? 'date' : prefs.sortKey;
  const rows: any[] = useMemo(
    () =>
      sortRows(
        activeTab === 'segmentations' ? filterByStatus(baseRows, prefs.status) : baseRows,
        sortKey,
        prefs.sortDir
      ),
    [baseRows, activeTab, prefs.status, sortKey, prefs.sortDir]
  );
  // Keypoint rows are matched by their frame (that is what the viewer shows).
  const rowIcid = (record: any) => (activeTab === 'keypoints' ? record.frame?.icid : record.icid);
  const selectable = useMemo(
    () =>
      rows.filter((r: any) => (activeTab === 'keypoints' ? r.frame?.signed_url : r.paths?.length)),
    [rows, activeTab]
  );
  const selectedPos = selectable.findIndex((r: any) => selectedIcid && rowIcid(r) === selectedIcid);
  const selectRow = (record: any) => {
    if (!record) return;
    if (activeTab === 'keypoints') selectKeypoints(record);
    else selectMask(record);
  };

  const selectMask = (record: any) => {
    if (!(record.paths && record.paths.length)) {
      toast.error('This segmentation has no outline data.', {
        position: 'top-right',
        autoClose: 3000,
      });
      return;
    }
    onSegmentationClick({
      maskIcid: record.icid,
      frameUrl: record.frame?.signed_url ?? '',
      frameIcid: record.frame?.icid ?? '',
      frameExtension: record.frame?.extension ?? 'png',
      paths: record.paths,
    });
  };

  const selectKeypoints = (record: KeypointCollectionLabel) => {
    if (!record.frame?.signed_url) {
      toast.error('Frame file does not exist.', { position: 'top-right', autoClose: 3000 });
      return;
    }
    if (typeof onKeypointClick === 'function') {
      const frameKeypoints = (record.keypoint_labels || []).map(kp => ({
        ...kp,
        frame: record.frame,
        keypoint_collection_icid: record.icid,
      }));
      onKeypointClick({
        frameUrl: record.frame.signed_url,
        frameIcid: record.frame.icid,
        frameExtension: 'png',
        keypoints: frameKeypoints,
      });
    }
  };

  const playColumn = {
    title: '',
    key: 'play',
    render: () => (
      <button style={{ background: 'none', border: 'none', cursor: 'pointer' }}>
        <span
          style={{
            transform: 'rotate(90deg)',
            backgroundColor: 'grey',
            padding: '2px',
            borderRadius: '50%',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 24,
            height: 24,
          }}
        >
          <PlayIcon size={16} />
        </span>
      </button>
    ),
  };

  const columnsSegmentations: ColumnsType<ApiMediaData> = [
    playColumn,
    {
      title: 'Icid',
      dataIndex: 'icid',
      key: 'icid',
      render: (_: string, record) => (
        <ClickableUUID
          role="button"
          tabIndex={0}
          onClick={e => {
            e.stopPropagation();
            selectRow(record);
          }}
          onKeyDown={e => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              selectRow(record);
            }
          }}
        >
          {record.icid}
        </ClickableUUID>
      ),
    },
    {
      title: 'Technician',
      key: 'user',
      render: (_: any, record) => technicianName(record) || '—',
    },
    {
      title: 'Feature',
      key: 'feature',
      render: (_: any, record) => record.segmentation_class?.value ?? '—',
    },
    {
      title: 'Review',
      key: 'review',
      render: (_: any, record) => <ReviewBadge record={record} />,
    },
    {
      title: 'Frame #',
      key: 'frame',
      render: (_: any, record) => record.frame?.index ?? '',
    },
    {
      title: 'Date',
      dataIndex: 'created_at',
      key: 'created_at',
      render: (d: string) => new Date(d).toLocaleString(),
    },
  ];

  const shortDate = (d?: string) =>
    d
      ? new Date(d).toLocaleDateString(undefined, {
          month: 'short',
          day: 'numeric',
          year: '2-digit',
        })
      : '—';
  /**
   * Two stacked columns for the side dock (~300px): what you scan to pick an overlay.
   * Left: feature, then ICID · frame. Right: review chip (segmentations), then who · when.
   */
  const dockColumns = (kind: 'segmentations' | 'references' | 'keypoints'): ColumnsType<any> => [
    {
      title: 'Feature',
      key: 'feature',
      render: (_: any, record: any) => {
        const fr = record.frame?.index;
        return (
          <span className="ax-dock-feature">
            <b>{featureName(record) || '—'}</b>
            <span className="sub">
              <ClickableUUID
                role="button"
                tabIndex={0}
                onClick={e => {
                  e.stopPropagation();
                  selectRow(record);
                }}
                onKeyDown={e => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    selectRow(record);
                  }
                }}
              >
                {record.icid}
              </ClickableUUID>
              {fr != null && fr !== '' ? ` · fr ${fr}` : ''}
            </span>
          </span>
        );
      },
    },
    {
      title: kind === 'segmentations' ? 'Review · by' : kind === 'references' ? 'Date' : 'By',
      key: 'by',
      align: 'right' as const,
      render: (_: any, record: any) => {
        const tech = kind === 'references' ? '' : technicianName(record);
        const who = [tech, shortDate(record.created_at)].filter(Boolean).join(' · ');
        return (
          <span className="ax-dock-by">
            {kind === 'segmentations' && <ReviewBadge record={record} />}
            <span className="sub" title={who}>
              {who}
            </span>
          </span>
        );
      },
    },
  ];

  const columnsReferences: ColumnsType<any> = [
    playColumn,
    {
      title: 'Icid',
      dataIndex: 'icid',
      key: 'icid',
      render: (_: string, record) => (
        <ClickableUUID
          role="button"
          tabIndex={0}
          onClick={e => {
            e.stopPropagation();
            selectRow(record);
          }}
          onKeyDown={e => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              selectRow(record);
            }
          }}
        >
          {record.icid}
        </ClickableUUID>
      ),
    },
    {
      title: 'Feature',
      key: 'feature',
      render: (_: any, record) => record.segmentation_class?.value ?? '—',
    },
    {
      title: 'Frame #',
      key: 'frame',
      render: (_: any, record) => record.frame?.index ?? '',
    },
    {
      title: 'Date',
      dataIndex: 'created_at',
      key: 'created_at',
      render: (d: string) => new Date(d).toLocaleString(),
    },
  ];

  const columnsKeypointCollections: ColumnsType<KeypointCollectionLabel> = [
    {
      title: 'ICID',
      dataIndex: 'icid',
      key: 'icid',
      render: (_: any, record) => (
        <ClickableUUID
          role="button"
          tabIndex={0}
          onClick={e => {
            e.stopPropagation();
            selectRow(record);
          }}
          onKeyDown={e => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              selectRow(record);
            }
          }}
        >
          {record.icid}
        </ClickableUUID>
      ),
    },
    {
      title: 'Frame ICID',
      key: 'frame_icid',
      render: (_: any, record) => record.frame?.icid ?? '—',
    },
    {
      title: 'Technician',
      key: 'user',
      render: (_: any, record) => {
        const user = record.user;
        if (user?.first_name && user?.last_name) {
          return `${user.first_name} ${user.last_name}`;
        } else if (user?.first_name) {
          return user.first_name;
        } else if (user?.last_name) {
          return user.last_name;
        }
        return '—';
      },
    },
    {
      title: 'Keypoint Collection Class',
      key: 'keypoint_collection_class',
      render: (_: any, record) => record.keypoint_collection_class?.value ?? '—',
    },
  ];

  const columnsPerspectives: ColumnsType<Perspective> = [
    playColumn,
    {
      title: 'Icid',
      dataIndex: 'icid',
      key: 'icid',
      render: (_: unknown, record: Perspective) => {
        const icid = record.view_label?.icid ?? record.quality_label?.icid ?? '—';
        return <ClickableUUID>{icid}</ClickableUUID>;
      },
    },
    {
      title: 'Quality',
      key: 'quality_label',
      render: (_: any, rec: any) => rec.quality_label?.quality_class ?? '—',
    },
    {
      title: 'User',
      key: 'user',
      render: (_: any, rec: any) => rec.user?.first_name ?? '—',
    },
    {
      title: 'View',
      key: 'view_class',
      render: (_: any, rec: any) => rec.view_class?.view ?? '—',
    },
  ];

  return (
    <SegmentsContainer className={dock ? 'ax-labels-dock' : undefined}>
      <HeaderRow>
        <SegmentsLabel onClick={() => setIsExpanded(!isExpanded)}>
          <ExpandIcon>{isExpanded ? '▲' : '▼'}</ExpandIcon>
          Labels
        </SegmentsLabel>

        {/* Buttons now effectively act as the "Trigger" for the fetch */}
        <TabButton
          active={activeTab === 'perspectives'}
          onClick={() => {
            setActiveTab('perspectives');
            setIsExpanded(true);
          }}
        >
          Perspectives
        </TabButton>
        <TabButton
          active={activeTab === 'segmentations'}
          onClick={() => {
            setActiveTab('segmentations');
            setIsExpanded(true);
          }}
        >
          Segmentations
        </TabButton>
        <TabButton
          active={activeTab === 'keypoints'}
          onClick={() => {
            setActiveTab('keypoints');
            setIsExpanded(true);
          }}
        >
          Keypoints
        </TabButton>
        <TabButton
          active={activeTab === 'references'}
          onClick={() => {
            setActiveTab('references');
            setIsExpanded(true);
          }}
          title={dock ? 'Segmentation References' : undefined}
        >
          {dock ? 'References' : 'Segmentation References'}
        </TabButton>
        {dock && onToggleSide && (
          <TabButton
            active={false}
            className="ax-side-toggle"
            onClick={onToggleSide}
            title="Put the labels back below the viewer"
          >
            Move below ⤓
          </TabButton>
        )}
      </HeaderRow>
      {isExpanded && activeTab !== 'perspectives' && (
        <div className="ax-label-controls">
          <div className="grp" role="group" aria-label="Sort labels">
            <span className="lbl">Sort</span>
            {SORTS.filter(o => !(activeTab === 'references' && o.key === 'technician')).map(o => {
              const on = sortKey === o.key;
              return (
                <TabButton
                  key={o.key}
                  active={on}
                  aria-pressed={on}
                  title={on ? 'Click again to reverse' : `Sort by ${o.label.toLowerCase()}`}
                  onClick={() =>
                    setPrefs(
                      on
                        ? { sortKey: o.key, sortDir: prefs.sortDir === 'asc' ? 'desc' : 'asc' }
                        : { sortKey: o.key, sortDir: o.key === 'date' ? 'desc' : 'asc' }
                    )
                  }
                >
                  {o.label}
                  {on ? (prefs.sortDir === 'asc' ? ' ↑' : ' ↓') : ''}
                </TabButton>
              );
            })}
          </div>
          {activeTab === 'segmentations' && (
            <div className="grp" role="group" aria-label="Filter by review">
              <span className="lbl">Review</span>
              {STATUSES.map(o => (
                <TabButton
                  key={o.key}
                  active={prefs.status === o.key}
                  aria-pressed={prefs.status === o.key}
                  onClick={() => setPrefs({ status: o.key })}
                >
                  {o.label} <span className="n">{counts[o.key]}</span>
                </TabButton>
              ))}
            </div>
          )}
        </div>
      )}
      {isExpanded && selectable.length > 0 && (
        <div className="ax-label-stepper" role="group" aria-label="Step through overlays">
          <TabButton
            active={false}
            disabled={selectedPos <= 0}
            onClick={() => selectRow(selectable[selectedPos - 1])}
            aria-label="Previous overlay"
          >
            ‹
          </TabButton>
          <span className="pos">
            {selectedPos >= 0
              ? `${selectedPos + 1} / ${selectable.length}`
              : `${selectable.length} overlays`}
          </span>
          <TabButton
            active={false}
            disabled={selectedPos >= selectable.length - 1}
            onClick={() => selectRow(selectable[Math.max(-1, selectedPos) + 1])}
            aria-label="Next overlay"
          >
            ›
          </TabButton>
          {!dock && <span className="hint">Click a row to show it on the frame</span>}
          {!dock && onToggleSide && (
            <TabButton
              active={false}
              className="ax-side-toggle"
              onClick={onToggleSide}
              title="Show the labels next to the viewer while you step through them"
            >
              Show beside viewer ⇥
            </TabButton>
          )}
        </div>
      )}
      {isExpanded && (
        <>
          <StyledTable<any>
            className={dock ? 'ax-dock-table' : undefined}
            columns={
              activeTab === 'perspectives'
                ? columnsPerspectives
                : dock
                  ? dockColumns(activeTab)
                  : activeTab === 'keypoints'
                    ? columnsKeypointCollections
                    : activeTab === 'references'
                      ? columnsReferences
                      : columnsSegmentations
            }
            dataSource={activeTab === 'perspectives' ? perspectivesData : rows}
            locale={{
              emptyText:
                activeTab === 'segmentations' && prefs.status !== 'all' && baseRows.length
                  ? `No ${prefs.status} segmentations`
                  : 'No data',
            }}
            rowClassName={(record: any) => {
              const classes: string[] = [];
              if (activeTab === 'segmentations' || activeTab === 'references') {
                if (!(record.paths && record.paths.length)) classes.push('missing-paths-row');
              }
              if (activeTab !== 'perspectives') classes.push('selectable-row');
              if (selectedIcid && rowIcid(record) === selectedIcid) classes.push('selected-row');
              return classes.join(' ');
            }}
            onRow={(record: any) =>
              activeTab === 'perspectives' ? {} : { onClick: () => selectRow(record) }
            }
            scroll={dock ? undefined : { x: 'max-content', y: 280 }}
            rowKey={(record: any, index: number) =>
              record.uuid ?? record.view_label?.uuid ?? record.quality_label?.uuid ?? `row-${index}`
            }
            loading={
              activeTab === 'keypoints'
                ? isKeypointFetching
                : activeTab === 'perspectives'
                  ? isPersFetching
                  : activeTab === 'references'
                    ? isSegRefsFetching
                    : isSegFetching
            }
            pagination={false}
            size="small"
          />
        </>
      )}
    </SegmentsContainer>
  );
};

export default Segments;
